<script lang="ts">
	import classNames from 'classnames';
	import { tick } from 'svelte';
	import type { Attachment } from 'svelte/attachments';
	import { twMerge } from 'tailwind-merge';
	import { getAuthContext } from '../../NewsHeader/AuthDialogs/utils/getAuthContext/getAuthContext';
	import { ArtistCardTag } from '#lib/features/news/components/NewsCard/ArtistCardTag/index.js';
	import { GeoCardTag } from '#lib/features/news/components/NewsCard/GeoCardTag/index.js';
	import { HeniCardTag } from '#lib/features/news/components/NewsCard/HeniCardTag/index.js';
	import type { SavedTags } from '#lib/websites/news/types.js';
	import { page } from '$app/state';
	import { ChevronLeftIcon } from '$global/assets/icons/ChevronLeftIcon';
	import { ChevronRightIcon } from '$global/assets/icons/ChevronRightIcon';
	import { Container } from '$global/components/Container';

	const dataCyPrefix = 'news-saved-tags';
	const { updatingUserTags, removeUserTag } = getAuthContext();

	let divRef: HTMLDivElement | undefined = $state();

	interface Props {
		// Optional: the bar renders before user prefs resolve, and the /news
		// layout load yields `undefined` default tags if its fetch failed.
		tags?: SavedTags | undefined;
		getHandleClickCross?:
			| undefined
			| ((userPref: Parameters<typeof removeUserTag>[0]) => () => void);
		forceShowGlobalNav?: boolean | undefined;
	}

	let {
		tags,
		getHandleClickCross = undefined,
		forceShowGlobalNav = undefined,
	}: Props = $props();

	// The URL param is the fallback authority: on error pages (and any route
	// whose load doesn't provide showGlobalNav) page.data is empty, and
	// app-mode (global_nav=0) visitors must stay on navless links.
	let showGlobalNav = $derived(
		forceShowGlobalNav ??
			page.data.showGlobalNav ??
			page.url.searchParams.get('global_nav') !== '0'
	);

	const handleClickLeftIcon = () => {
		if (divRef) {
			divRef.scrollTo(divRef.scrollLeft - 500, 0);
		}
	};

	const handleClickRightIcon = () => {
		if (divRef) {
			divRef.scrollTo(divRef.scrollLeft + 500, 0);
		}
	};

	// The chevrons show only while the tags overflow the bar. This used to be a
	// `$derived` over `divRef.scrollWidth` and `bind:clientWidth`, which forced
	// layout while deriveds ran and tripped `state_unsafe_mutation` (Sentry
	// #2814). A ResizeObserver measures instead, writing the state a frame
	// later, outside any flush. The tags are observed as well as the bar, so a
	// tag that grows (e.g. once its web font loads) re-measures even when the
	// bar's own width is unchanged.
	let showChevrons = $state(false);

	const measureOverflow: Attachment<HTMLDivElement> = (element) => {
		let frame: number | undefined;

		const observer = new ResizeObserver(() => {
			if (frame !== undefined) {
				cancelAnimationFrame(frame);
			}

			frame = requestAnimationFrame(() => {
				frame = undefined;
				showChevrons = element.scrollWidth > element.clientWidth;
			});
		});

		observer.observe(element);
		Array.from(element.children).forEach((child) => observer.observe(child));

		return () => {
			observer.disconnect();

			if (frame !== undefined) {
				cancelAnimationFrame(frame);
			}
		};
	};

	// A newly saved tag is appended at the end of the list (see addUserTag), so
	// once the refreshed list lands the bar scrolls fully right to reveal it
	// (HB-1287). Only a growing count triggers it: the initial render, a
	// removal and the default→user tags swap all keep the scroll position.
	// Plain `let`, not `$state`: it is bookkeeping for the effect, and making it
	// reactive would re-run the effect on its own write.
	let previousTagCount: number | undefined;

	$effect(() => {
		const tagCount = tags?.length ?? 0;
		const hasAddedTag =
			previousTagCount !== undefined && tagCount > previousTagCount;

		previousTagCount = tagCount;

		if (!hasAddedTag) {
			return;
		}

		// `{#key}` remounts the scroller on the same change, so `divRef` is read
		// only after the flush has settled — inside the effect it could still be
		// the detached element, and depending on it would re-run this effect
		// with the count already recorded.
		tick().then(() => {
			const scroller = divRef;

			if (scroller) {
				scroller.scrollTo({ left: scroller.scrollWidth });
			}
		});
	});
</script>

<!-- The bar renders even with no saved tags: the HENI filter pill is always
present in it (HB-948), so it is no longer gated on `tags?.length`. -->
<!-- There's a bug with scrollWidth being actually smaller when a tag is added, hence the complete rerendering -->
{#key tags?.length}
	<div
		class={classNames(
			'fixed top-[12.1875rem] z-[25] flex h-[3.25rem] w-full items-center border-b bg-white py-2',
			{
				'hidden sm:top-[6.0625rem] sm:block sm:py-3': true,
			},
			{
				'lg:top-[3.5625rem]': !showGlobalNav,
			},
			{
				'lg:top-[9.5625rem]': showGlobalNav,
			}
		)}
	>
		<Container dataCy={dataCyPrefix}>
			<div
				bind:this={divRef}
				{@attach measureOverflow}
				id="saved-tags-bar-scroll"
				class={classNames(
					'no-scrollbar relative flex gap-4 overflow-scroll scroll-smooth',
					{
						'pr-8 pl-8': showChevrons,
					}
				)}
			>
				<!-- Pinned outside the loop: HENI is a fixed filter rather than a
					removable user tag, and always leads the bar (HB-948). -->
				<HeniCardTag
					dataCy={dataCyPrefix}
					class="h-[28px]"
					{forceShowGlobalNav}
				/>

				{#each tags ?? [] as tag (tag?.id || `${tag?.type}-${tag?.name}`)}
					{#if tag?.type === 'ARTIST'}
						<ArtistCardTag
							showNewVersion={false}
							dataCy={dataCyPrefix}
							name={tag?.name}
							class={classNames('h-[28px] border-gray-200', {
								'opacity-40': $updatingUserTags,
							})}
							showCross={!!getHandleClickCross}
							onClickCross={getHandleClickCross
								? getHandleClickCross(tag)
								: undefined}
							{forceShowGlobalNav}
						/>
					{/if}

					{#if tag?.type === 'GEOTAG'}
						<GeoCardTag
							showNewVersion={false}
							dataCy={dataCyPrefix}
							name={tag?.name}
							class={classNames('h-[28px] border-gray-200', {
								'opacity-40': $updatingUserTags,
							})}
							showCross={!!getHandleClickCross}
							onClickCross={getHandleClickCross
								? getHandleClickCross(tag)
								: undefined}
							{forceShowGlobalNav}
						/>
					{/if}
				{/each}
			</div>
		</Container>
	</div>

	{#if showChevrons}
		<button
			onclick={handleClickLeftIcon}
			aria-label="Scroll saved tags left"
			class={twMerge(
				'chevron-left bg-gray-0 fixed top-[12.1875rem] left-3.5 z-[26] flex h-[3.125rem] w-7 items-center justify-start min-[900px]:top-[6.8125rem] sm:top-[13.3125rem] sm:left-6 lg:top-[10.3125rem] xl:left-[calc((100vw-1280px)/2+1.5rem)]',
				classNames(
					{
						'hidden outline-none min-[900px]:top-[6.125rem] sm:top-[6.125rem] sm:left-5 sm:flex': true,
					},
					{
						'lg:top-[9.625rem]': showGlobalNav,
					},
					{
						'lg:top-[3.625rem]': !showGlobalNav,
					}
				)
			)}
		>
			<ChevronLeftIcon class="h-4 w-4" />
		</button>

		<button
			onclick={handleClickRightIcon}
			aria-label="Scroll saved tags right"
			class={twMerge(
				'chevron-right bg-gray-0 fixed top-[12.1875rem] right-3.5 z-[26] flex h-[3.125rem] w-7 items-center justify-end outline-none min-[900px]:top-[6.8125rem] sm:top-[13.3125rem] sm:right-5 lg:top-[10.3125rem] xl:right-[calc((100vw-1280px)/2+1.25rem)]',
				classNames(
					{
						'hidden outline-none min-[900px]:top-[6.125rem] sm:top-[6.125rem] sm:flex': true,
					},
					{
						'lg:top-[9.625rem]': showGlobalNav,
					},
					{
						'lg:top-[3.625rem]': !showGlobalNav,
					}
				)
			)}
		>
			<ChevronRightIcon class="h-4 w-4" />
		</button>
	{/if}
{/key}

<style>
	.chevron-left {
		background: linear-gradient(
			to right,
			rgba(255, 255, 255, 1) 50%,
			rgba(255, 255, 255, 0)
		);
	}

	.chevron-right {
		background: linear-gradient(
			to left,
			rgba(255, 255, 255, 1) 50%,
			rgba(255, 255, 255, 0)
		);
	}
</style>
